import "./TopNavigationBar.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../store";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";
import { ProfileSelectorDropdown } from "../ProfileSelectorDropdown/ProfileSelectorDropdown";
import { NewPeriodSelector } from "../NewPeriodSelector/NewPeriodSelector";
import { PageTitleSection } from "../../../common/components/PageTitleSection/PageTitleSection";
import defaultContent from "../../../content/navigation";
import { useTranslation } from "react-i18next";

const ACTION_REPORT_ROUTES: string[] = [
  ROUTE_PATHS.RENEWALS,
  ROUTE_PATHS.WHOS_NEW_ACTION_REPORT,
  ROUTE_PATHS.WHOS_CLOSE_ACTION_REPORT,
  ROUTE_PATHS.BIRTHDAYS_ACTION_REPORT,
  ROUTE_PATHS.NON_BONUSABLE_BV_ACTION_REPORT,
  ROUTE_PATHS.CVR,
  ROUTE_PATHS.IBO_ONBOARDING,
  ROUTE_PATHS.CBR,
  ROUTE_PATHS.SAQ_ACTION_REPORT,
];

interface TopNavigationBarProps {
  displayPeriodSelector: boolean;
}

export const TopNavigationBar = ({ displayPeriodSelector }: TopNavigationBarProps) => {
  const { headless } = useAppSelector((state) => state.loginLocalStorage);
  const { sideNavExpanded } = useAppSelector((state) => state.uiState);
  const { pathname } = useLocation();
  const isActionReport = ACTION_REPORT_ROUTES.includes(pathname);

  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const skipScrollHandler = useRef(false);
  const { t } = useTranslation(["navigation"]);

  const handleScroll = useCallback(() => {
    // Skip scroll handling during table row scrolling or when dropdowns are open
    if (
      skipScrollHandler.current ||
      document.querySelector(".NewPeriodSelector__dropdown") ||
      window.__tableRowScrollInProgress
    )
      return;

    const currentScrollY = window.scrollY;
    const scrollingUp = currentScrollY < lastScrollY.current;

    setIsVisible(scrollingUp || currentScrollY <= 0);
    lastScrollY.current = currentScrollY;
  }, [skipScrollHandler]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <header
      className={`TopNavigationBar${!headless && sideNavExpanded ? " TopNavigationBar--expanded" : ""}${isVisible ? "" : " TopNavigationBar--hidden"}${headless ? " TopNavigationBar--headless" : ""}${isActionReport ? " TopNavigationBar--action-report" : ""}`}
      role="banner"
      data-testid="top-navigation-bar"
    >
      <div className="TopNavigationBar__bar">
        <div className="TopNavigationBar__container">
          <div className="TopNavigationBar__left" id="title-portal">
            {pathname === ROUTE_PATHS.LOS && (
              <PageTitleSection
                title={`${t("linkTitle.losShort", (defaultContent.linkTitle as Record<string, string>).losShort)}`}
              />
            )}
          </div>
          <div className="TopNavigationBar__right">
            {displayPeriodSelector && (
              <div className="TopNavigationBar__right--period-selector">
                <NewPeriodSelector />
              </div>
            )}
            {!headless && <ProfileSelectorDropdown />}
          </div>
        </div>
      </div>
    </header>
  );
};
